import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { bumpUnitsRefresh, markRefreshSource, setWsStatus, type WsStatus } from "@/lib/liveRefresh";

interface WsEvent {
  ev: string;       // "session.started" | "session.ended" | "emulator.action"
  unit_id?: string;
  session_id?: string;
  path?: string;    // emulator proxy path
}

const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 15_000;

// Session events arrive from the whole fleet; refetching the fleet list on each
// one would out-poll the old 5 s timer. Coalesce: at most one units/stats
// invalidation per window, the trailing one carrying everything since. A
// reconnect's catch-up refetch goes through the same window — a flapping socket
// (backoff resets to 1 s on every open) must not turn into a 1 s fleet poll.
export const UNITS_INVALIDATE_MIN_GAP_MS = 10_000;

function toWsUrl(path: string): string {
  const loc = window.location;
  const proto = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${loc.host}${path}`;
}

export function useWsEvents(): WsStatus {
  const qc = useQueryClient();
  const [status, setStatus] = useState<WsStatus>("idle");
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    let cancelled = false;
    let delay = INITIAL_RETRY_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let unitsTimer: ReturnType<typeof setTimeout> | null = null;
    let unitsTimerSource: "ws" | "reconnect" = "ws";
    let lastUnitsInvalidate = 0;
    let everOpened = false;

    const report = (s: WsStatus) => {
      setStatus(s);
      setWsStatus(s);
    };

    const invalidateUnitsNow = (source: "ws" | "reconnect") => {
      lastUnitsInvalidate = Date.now();
      if (unitsTimer !== null) {
        clearTimeout(unitsTimer);
        unitsTimer = null;
      }
      markRefreshSource(source);
      qc.invalidateQueries({ queryKey: ["units"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      bumpUnitsRefresh();
    };

    const invalidateUnitsCoalesced = (source: "ws" | "reconnect") => {
      const wait = lastUnitsInvalidate + UNITS_INVALIDATE_MIN_GAP_MS - Date.now();
      if (wait <= 0) {
        invalidateUnitsNow(source);
        return;
      }
      if (source === "reconnect") unitsTimerSource = "reconnect";
      if (unitsTimer === null) {
        unitsTimer = setTimeout(() => {
          unitsTimer = null;
          const pending = unitsTimerSource;
          unitsTimerSource = "ws";
          if (!cancelled) invalidateUnitsNow(pending);
        }, wait);
      }
    };

    function connect() {
      if (cancelled) return;
      report("connecting");

      const ws = new WebSocket(toWsUrl("/ws/events"));
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        delay = INITIAL_RETRY_MS;
        report("open");
        if (everOpened) {
          // Events published while disconnected were lost: one catch-up refetch.
          invalidateUnitsCoalesced("reconnect");
          qc.invalidateQueries({ queryKey: ["sessions"] });
          qc.invalidateQueries({ queryKey: ["active-sessions"] });
        }
        everOpened = true;
      };

      ws.onmessage = (ev) => {
        if (cancelled) return;
        let msg: { type: string; data?: WsEvent[] };
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }

        if (msg.type === "events" && msg.data) {
          let invalidateUnits = false;
          let invalidateSessions = false;
          let invalidateEmulator = false;

          for (const e of msg.data) {
            if (e.ev === "session.started" || e.ev === "session.ended") {
              invalidateUnits = true;
              invalidateSessions = true;
            }
            if (e.ev === "emulator.action") {
              invalidateEmulator = true;
            }
          }

          if (invalidateUnits) {
            invalidateUnitsCoalesced("ws");
          }
          if (invalidateSessions) {
            qc.invalidateQueries({ queryKey: ["sessions"] });
            qc.invalidateQueries({ queryKey: ["active-sessions"] });
          }
          if (invalidateEmulator) {
            qc.invalidateQueries({ queryKey: ["emulator-units"] });
          }
        }
      };

      ws.onerror = () => {
        // onclose fires after onerror
      };

      ws.onclose = () => {
        if (cancelled) return;
        wsRef.current = null;
        report("error");
        timer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, MAX_RETRY_MS);
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      if (unitsTimer !== null) clearTimeout(unitsTimer);
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
      setWsStatus("idle");
    };
  }, [qc]);

  return status;
}
